import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { isPast, parse } from 'date-fns';
import { Observable, timer } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';

export interface CountdownResult {
  timeString: string;
  isExpired: boolean;
  secondsRemaining: number;
}

@Injectable({
  providedIn: 'root',
})
export class CountdownService implements OnDestroy {
  private activeSubscriptions = new Set<{ unsubscribe: () => void }>();
  private ngZone = inject(NgZone);

  ngOnDestroy(): void {
    this.activeSubscriptions.forEach((sub) => sub.unsubscribe());
    this.activeSubscriptions.clear();
  }

  /**
   * Creates a countdown Observable for a given target date
   * @param targetDate - ISO string of the target date
   * @param showExpired - Whether to show "Expired" when time has passed
   * @param suffix - Text to append after the time display
   * @returns Observable that emits countdown updates every second
   */
  createCountdown(targetDate: string, showExpired = true, suffix = ''): Observable<string> {
    const parsedDate = parse(targetDate);

    return new Observable<string>((observer) => {
      const subscription = this.ngZone.runOutsideAngular(() => {
        return timer(0, 1000)
          .pipe(
            map(() => {
              const now = new Date();
              const seconds = Math.round((parsedDate.getTime() - now.getTime()) / 1000);

              if (isPast(parsedDate) || seconds <= 0) {
                return showExpired ? 'Expired' : '';
              }

              const timeString = this.formatTimeString(seconds);
              return suffix ? `${timeString} ${suffix}` : timeString;
            }),
            takeWhile((result) => result !== 'Expired' && result !== '', true)
          )
          .subscribe({
            next: (result) => {
              // Run the observer.next inside Angular zone to trigger change detection
              this.ngZone.run(() => {
                observer.next(result);
              });
            },
            error: (err) => this.ngZone.run(() => observer.error(err)),
            complete: () =>
              this.ngZone.run(() => {
                observer.complete();
                subscription.unsubscribe();
              }),
          });
      });

      // Track subscription for cleanup
      this.activeSubscriptions.add(subscription);
    });
  }

  private formatTimeString(seconds: number): string {
    if (Number.isNaN(seconds)) {
      return 'Date formatting error';
    }

    const minutes = Math.round(seconds / 60);
    const hours = Math.round(minutes / 60);
    const days = Math.round(hours / 24);
    const months = Math.round(days / 30.416);
    const years = Math.round(days / 365);

    if (seconds <= 60) {
      return `${seconds} seconds`;
    } else if (seconds <= 90) {
      return 'a minute';
    } else if (minutes <= 45) {
      return `${minutes} minutes`;
    } else if (minutes <= 90) {
      return 'an hour';
    } else if (hours <= 22) {
      return `${hours} hours`;
    } else if (hours <= 36) {
      return 'a day';
    } else if (days <= 25) {
      return `${days} days`;
    } else if (days <= 45) {
      return 'a month';
    } else if (days <= 345) {
      return `${months} months`;
    } else if (days <= 545) {
      return 'a year';
    } else {
      return `${years} years`;
    }
  }
}
